import {
  ClientConfig,
  createClient,
  Event,
  ExperimentTrackingMetadata,
  Page,
} from '@stripe-internal/mkt-analytics';
import {STRIPE_ENV} from 'mkt-ssr/lib/env';
import {
  Country,
  PrefixablePair,
  stripGlobalizationPrefix,
} from 'mkt-ssr/lib/globalization';
import {
  ClientMethods as BaseClientMethods,
  MOCK_ANALYTICS_CLIENT,
  NOOP_ANALYTICS_CLIENT,
} from '@stripe-internal/nautilus-components';

// Ideally this should be extracted from the mkt-srv
// config and environment based so that we are consistent about
// what is used across envs and across rendering types,
// however right now this is a fixed ID
// used in every environment and adding yet another generated file
// task is too much overhead for this.
export const GTM_ID = 'GTM-WK8882T';

export interface ClientMethods extends BaseClientMethods {
  trackPage(page: Page): Promise<void>;
  trackEvent(event: Event): Promise<void>;
  setExperimentTreatment(treatment: ExperimentTrackingMetadata): Promise<void>;
  get config(): ClientConfig | undefined;
}

export {MOCK_ANALYTICS_CLIENT};

export function getGTMFrameURL(env: STRIPE_ENV) {
  return [STRIPE_ENV.DEV, STRIPE_ENV.QA].includes(env)
    ? 'https://qa-b.stripecdn.com/stripethirdparty-srv/assets/'
    : 'https://b.stripecdn.com/stripethirdparty-srv/assets/';
}

// NB: While we don't strictly need this wrapper object
// and could export the function directly, it makes mocking
// out the client with stubs in tests *significantly* easier.
// As we do not expect more than just the context code
// to need to access this behavior directly the small indirection
// is acceptable.
export const ClientFactory = {
  createAnalyticsClient({
    isSSR,
    env,
    globalizationPair,
    geoIPCountry,
    createAnalyticsClientFactory = createClient,
  }: {
    isSSR: boolean;
    env: STRIPE_ENV;
    globalizationPair: PrefixablePair;
    geoIPCountry?: Country;
    createAnalyticsClientFactory?: typeof createClient;
  }): ClientMethods {
    const gtmFrameUrl = getGTMFrameURL(env);
    if (isSSR) {
      return MOCK_ANALYTICS_CLIENT as ClientMethods;
    } else if (
      window.location.search.includes('__auth') &&
      window.location.search.includes('__disableAnalytics')
    ) {
      return NOOP_ANALYTICS_CLIENT as ClientMethods;
    } else {
      const client = createAnalyticsClientFactory({
        getLocaleInfo() {
          // NB: This is really not an accurate methodology of doing this, but
          // this is the same logic that is used by mkt-srv to determine these
          // values for analytics and as a result we need to be consistent,
          // and changing what values are passed to the analytics library
          // is a fraught path, so for now we do this "wrong" and live with it.
          const unglobalizedPath = stripGlobalizationPrefix(
            window.location.pathname,
            globalizationPair,
          );
          return {
            siteLanguage: globalizationPair.locale.iso6391,
            siteLocale: globalizationPair.country.code,
            userGeoIpCountry: geoIPCountry?.code || '',
            unglobalizedPath,
          };
        },
        gtmFrameUrl,
        gtmId: GTM_ID,
        environment: env,
        deferLoading: true,
      });
      client.initialize();
      return client as ClientMethods;
    }
  },
};
